import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody, Button, Autocomplete, TablePagination, IconButton, TableContainer, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { collection, setDoc, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase'; // Adjust path as needed
import { useLocation } from 'react-router-dom';
import { textFieldStyles } from '../../Login/LoginPageStyles';

const AssignTasks = () => {
  const [taskName, setTaskName] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchText, setSearchText] = useState('');
  const location = useLocation();
  const { user } = location.state || {}; // Retrieve the user prop

  useEffect(() => {
    // Fetch employees from Firestore
    const fetchEmployees = async () => {
      try {
        const employeesCollection = collection(db, 'employees');
        const employeesSnapshot = await getDocs(employeesCollection);
        const employeesList = employeesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setEmployees(employeesList);
      } catch (error) {
        console.error('Error fetching employees: ', error);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    // Real-time listener for assigned tasks
    const tasksCollection = collection(db, 'assignedTasks');
    const unsubscribe = onSnapshot(tasksCollection, (snapshot) => {
      const tasksList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAssignedTasks(tasksList);
    }, (error) => {
      console.error('Error fetching tasks: ', error);
    });

    // Cleanup on unmount
    return () => unsubscribe();
  }, []);

  // Function to generate a random 5-digit task ID and ensure it's unique
  const generateTaskId = () => {
    const newId = Math.floor(10000 + Math.random() * 90000).toString();
    const idExists = assignedTasks.some(task => task.taskId === newId);
    return idExists ? generateTaskId() : newId;
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };
  
  const handleAssignTask = async () => {
    if (taskName && selectedEmployee) {
      const now = new Date();
      const formattedDate = formatDate(now);
  
      const newTask = {
        taskId: generateTaskId(),
        taskName,
        employee: selectedEmployee.name,
        employeeId: selectedEmployee.id,  // Store employee ID
        assignedBy: user?.name || 'Unknown', // Set assignedBy to current user's name
        status: 'pending', // Default status
        workHours: '0',
        createdDate: formattedDate, // Use formatted date here
      };
  
      try {
        // Create a reference to the new task document with the taskId as the document name
        const taskDocRef = doc(collection(db, 'assignedTasks'), newTask.taskId);
  
        // Set the new task document with the specified ID
        await setDoc(taskDocRef, newTask);
  
        // Reset the form after assignment
        setTaskName('');
        setSelectedEmployee(null);
      } catch (error) {
        console.error('Error adding task: ', error);
      }
    } else {
      alert('Please fill out all fields.');
    }
  };
  
  // Search function
  const filteredTasks = assignedTasks.filter(task =>
    task.employeeId.toLowerCase().includes(searchText.toLowerCase()) ||
    task.taskId.toLowerCase().includes(searchText.toLowerCase()) ||
    task.taskName.toLowerCase().includes(searchText.toLowerCase()) ||
    task.employee.toLowerCase().includes(searchText.toLowerCase())
  );

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Pagination data
  const paginatedTasks = filteredTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ flexGrow: 1, padding: 3, paddingTop: 0, marginTop: 0 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#39B54A' }}>
          Assign Tasks
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 3,
          marginTop: 2,
        }}
      >
        {/* Search Bar */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Task ID, Task, Employee ID or Employee"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ ...textFieldStyles, flexGrow: 1, marginRight: 1 }}
          />
          <IconButton>
            <SearchIcon />
          </IconButton>
        </Box>
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ tableLayout: 'auto', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ width: '50px' }}><b>Task ID<br />Date<br />Time</b></TableCell>
                <TableCell align="left" sx={{ width: '350px', whiteSpace: 'normal', wordWrap: 'break-word' }}><b>Task</b></TableCell>
                <TableCell align="left" sx={{ width: '300px' }}><b>Assign To</b></TableCell>
                <TableCell align="left"><b>Assigned By</b></TableCell>
                <TableCell align="center"><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="left">
                  {/* Task ID will be auto-generated, so no input field needed */}
                  <Typography variant="body1" color="textSecondary">
                    Auto-generated
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <TextField 
                    variant="outlined" 
                    size="small" 
                    placeholder="Enter Task"
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    fullWidth
                    multiline
                    sx={{ ...textFieldStyles}}
                  />
                </TableCell>
                <TableCell align="center">
                  <Autocomplete
                    options={employees}
                    getOptionLabel={(option) => `${option.id} - ${option.name}`}  // Show both ID and name
                    value={selectedEmployee}
                    onChange={(event, newValue) => setSelectedEmployee(newValue)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        variant="outlined" 
                        size="small" 
                        placeholder="Search or Select Employee"
                        sx={{ ...textFieldStyles}}
                      />
                    )}
                  />
                </TableCell>
                <TableCell><Typography variant="body1" color="textSecondary">{user.name}</Typography></TableCell>
                <TableCell align="center">
                  <Button 
                    variant="contained" 
                    sx={{ backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}
                    onClick={handleAssignTask}
                  >
                    Assign
                  </Button>
                </TableCell>
              </TableRow>
              {/* Display assigned tasks with pagination */}
              {paginatedTasks.map((task) => (
                <TableRow key={task.taskId}>
                  <TableCell align="left">{task.taskId} <br /> {task.createdDate}</TableCell>
                  <TableCell align="left" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{task.taskName}</TableCell>
                  <TableCell align="left">{`${task.employeeId} - ${task.employee}`}</TableCell>  {/* Show both ID and name */}
                  <TableCell align="left">{task.assignedBy}</TableCell>  {/* Show assignedBy */}
                  <TableCell align="center">Assigned</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Table Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTasks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default AssignTasks;
